import { useCallback, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { type ContextMenuItem, MediaCard, toast } from '@core/components';
import { useCrudList } from '@core/hooks/useCrudList';
import type { AlbSubtype } from '../../../api/albSubtype';
import { reorderAlbSubtypes } from '../../../api/albSubtype';
import {
  type Vestment,
  getAllVestments,
  reorderVestments,
} from '../../../api/vestment';
import AlbSubtypeDialog from '../../components/AlbSubtypeDialog';
import GroupedSortableSettings, {
  useSelectedGroup,
} from '../../components/GroupedSortableSettings';
import VestmentDialog from '../../components/VestmentDialog';
import { useAppContext } from '../../context/AppContext';
import { VestmentCategory } from '../../enum/vestmentCategory';
import { getCategory } from '../../enum/vestmentType';
import { getAlbFeatures } from '../../utils/alb';
import { getImageAspectRatio, getImageThumbnailUrl } from '../../utils/image';

const Albs = () => {
  const { refreshContext } = useAppContext();
  const [imageDialog, setImageDialog] = useState<{
    open: boolean;
    vestment: Vestment | null;
  }>({ open: false, vestment: null });
  const [subtypeDialog, setSubtypeDialog] = useState<{
    open: boolean;
    subtype: AlbSubtype | null;
  }>({ open: false, subtype: null });

  const loadAlbs = useCallback(
    async () =>
      (await getAllVestments()).filter(
        (vestment) =>
          !vestment.isHidden &&
          getCategory(vestment.type) === VestmentCategory.Alb
      ),
    []
  );
  const reorderAlbs = useCallback(
    (ids: number[]) => reorderVestments(VestmentCategory.Alb, ids),
    []
  );

  const {
    items: albs,
    setItems: setAlbs,
    loading,
    load,
    rowReorder,
  } = useCrudList<Vestment>({
    loadItems: loadAlbs,
    reorderItems: reorderAlbs,
    loadErrorMessage: 'Failed to load albs',
    reorderErrorMessage: 'Failed to reorder albs',
    refreshContext,
  });

  const { selectedGroup: selectedAlb, selectGroup: selectAlb } =
    useSelectedGroup(albs, 'alb');

  const subtypeMenuItems = (subtype: AlbSubtype): ContextMenuItem[] => [
    {
      label: 'Edit',
      onClick: () => setSubtypeDialog({ open: true, subtype }),
    },
  ];

  const onSubtypesReorder = (alb: Vestment, next: AlbSubtype[]) => {
    setAlbs((prev) =>
      prev.map((item) =>
        item.id === alb.id ? { ...item, albSubtypes: next } : item
      )
    );
    reorderAlbSubtypes(
      alb.id,
      next.map((subtype) => subtype.id)
    )
      .then(refreshContext)
      .catch(() => {
        toast.error('Failed to reorder subtypes');
        void load();
      });
  };

  return (
    <GroupedSortableSettings<Vestment, AlbSubtype>
      title="Albs"
      loading={loading}
      groups={albs}
      selectedGroup={selectedAlb}
      onSelectGroup={selectAlb}
      groupReorder={rowReorder}
      groupLabel="Alb"
      getGroupMenuItems={(vestment) => [
        {
          label: 'Edit',
          onClick: () => setImageDialog({ open: true, vestment }),
        },
      ]}
      emptyState={{ description: 'No albs are configured.' }}
      getItems={(alb) =>
        getAlbFeatures(alb.type)?.hasSubtypes ? alb.albSubtypes : []
      }
      getItemsCaption={(alb, count) => {
        if (!getAlbFeatures(alb.type)?.hasSubtypes) {
          return 'This alb has no subtypes.';
        }
        return `${count} subtype${count === 1 ? '' : 's'}`;
      }}
      onItemsReorder={onSubtypesReorder}
      renderItem={(subtype, onMenuClick) => (
        <MediaCard
          content={
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="caption" textAlign="center">
                {subtype.name}
              </Typography>
            </Box>
          }
          imageSrc={getImageThumbnailUrl(subtype)}
          aspectRatio={getImageAspectRatio(subtype) ?? 1}
          fullHeight
          onClick={() => setSubtypeDialog({ open: true, subtype })}
          onMenuClick={onMenuClick}
        />
      )}
      getItemMenuItems={subtypeMenuItems}
    >
      {imageDialog.vestment ? (
        <VestmentDialog
          open={imageDialog.open}
          onClose={() =>
            setImageDialog((current) => ({ ...current, open: false }))
          }
          vestment={imageDialog.vestment}
          onSuccess={() => void load()}
        />
      ) : null}
      <AlbSubtypeDialog
        key={subtypeDialog.subtype?.id}
        open={subtypeDialog.open}
        onClose={() => setSubtypeDialog({ open: false, subtype: null })}
        subtype={subtypeDialog.subtype}
        onSuccess={() => void load()}
      />
    </GroupedSortableSettings>
  );
};

export default Albs;
